import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBandeau } from '../BandeauContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';
import ActionBtn from '../admin/ActionBtn';
import Modal from '../common/Modal';

const API = process.env.REACT_APP_API_BASE_URL + '/schedule_api.php';

const DAYS = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const SLOT_TYPES = [
    { value: 'disponible', label: 'Révision', color: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30' },
    { value: 'cours',      label: 'Cours',    color: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30' },
    { value: 'sport',      label: 'Activité',  color: 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30' },
    { value: 'repos',      label: 'Repos',    color: 'bg-gray-400/20 text-gray-600 dark:text-gray-400 border-gray-400/30' },
    { value: 'sommeil',    label: 'Sommeil',  color: 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/30' },
    { value: 'autre',      label: 'Autre',    color: 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/30' },
];

function addMinutes(time, n) {
    const [h, m] = time.split(':').map(Number);
    const total = h * 60 + m + n;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function addHour(time) { return addMinutes(time, 60); }

// Qualité d'un créneau révision selon l'heure de début (chronobiologie)
function revisionQuality(startTime) {
    const m = startTime ? (parseInt(startTime) * 60 + parseInt(startTime.slice(3))) : 0;
    const h = m / 60;
    if ((h >= 6.5 && h <= 11) || (h >= 15 && h <= 19)) return 'optimal';
    if (h >= 21 || h < 5) return 'late';
    return 'ok';
}

const PERIOD_TYPES = [
    { value: 'normal',   label: 'Semaines normales' },
    { value: 'vacances', label: 'Vacances' },
    { value: 'examens',  label: 'Examens / révisions intensives' },
    { value: 'autre',    label: 'Autre' },
];


function slotTypeInfo(type) {
    return SLOT_TYPES.find(t => t.value === type) || SLOT_TYPES[0];
}

function formatTime(t) {
    return t ? t.slice(0, 5) : '';
}

function durationMin(start, end) {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
}



// ── Formulaire période ───────────────────────────────────────────────────────
function PeriodForm({ initial, onSave, onClose, onDelete }) {
    const [form, setForm] = useState({
        name: initial?.name ?? '',
        period_type: initial?.period_type ?? 'normal',
        start_date: initial?.start_date ?? '',
        end_date: initial?.end_date ?? '',
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const res = await apiClient(`${API}?action=save_period`, {
            method: 'POST',
            body: JSON.stringify({ ...form, id: initial?.id ?? 0 }),
        }).then(r => r.json());
        setSaving(false);
        if (res.success) onSave(res.id);
    };

    const handleDelete = async () => {
        if (!initial?.id || !window.confirm(`Supprimer la période « ${initial.name} » et tous ses créneaux ?`)) return;
        await apiClient(`${API}?action=delete_period`, {
            method: 'POST',
            body: JSON.stringify({ id: initial.id }),
        });
        onDelete();
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom de la période</label>
                <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select value={form.period_type} onChange={e => setForm(p => ({ ...p, period_type: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm">
                    {PERIOD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Début</label>
                    <input required type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fin</label>
                    <input required type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm" />
                </div>
            </div>
            <div className="flex justify-between items-center pt-2">
                {initial?.id ? (
                    <ActionBtn size="md" variant="danger" onClick={handleDelete}>Supprimer</ActionBtn>
                ) : <div />}
                <div className="flex gap-3">
                    <ActionBtn size="md" onClick={onClose}>Annuler</ActionBtn>
                    <ActionBtn size="md" variant="primary" type="submit" disabled={saving}>
                        {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </ActionBtn>
                </div>
            </div>
        </form>
    );
}

// ── Formulaire slot ──────────────────────────────────────────────────────────
function SlotForm({ initial, periodId, subjects, onSave, onClose, onDelete }) {
    const isNew = !initial?.id;
    const [form, setForm] = useState(() => {
        const isAutre = initial?.slot_type === 'cours' && !initial?.subject_id && initial?.label;
        const type  = initial?.slot_type ?? 'disponible';
        const start = initial?.start_time?.slice(0,5) ?? '08:00';
        const defaultEnd = initial?.end_time?.slice(0,5) ?? (type === 'disponible' ? addMinutes(start, 30) : addHour(start));
        return {
            days_of_week: [initial?.day_of_week ?? 1],
            start_time:   start,
            end_time:     defaultEnd,
            slot_type:    type,
            label:        initial?.label      ?? '',
            subject_id:   isAutre ? 'autre' : (initial?.subject_id ?? ''),
            frequency:    initial?.frequency  ?? 'weekly',
            repetitions:  initial?.repetitions ?? 1,
        };
    });

    const handleStartChange = (val) => {
        const repMin = form.slot_type === 'disponible' ? (seancePaliers.find(p => p.n === form.repetitions)?.min ?? 30) : 60;
        setForm(p => ({ ...p, start_time: val, end_time: addMinutes(val, repMin) }));
    };

    const handleTypeChange = (type) => {
        setForm(p => ({
            ...p,
            slot_type: type,
            end_time: type === 'disponible'
                ? addMinutes(p.start_time, 30)
                : p.end_time === addMinutes(p.start_time, 30) ? addHour(p.start_time) : p.end_time,
        }));
    };

    const calcDurMin = (start, end) => {
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        return (eh * 60 + em) - (sh * 60 + sm);
    };
    const seancePaliers = [
        { n: 1, min: 30, label: '30 min' },
        { n: 2, min: 60, label: '1h' },
        { n: 3, min: 90, label: '1h30' },
        { n: 4, min: 120, label: '2h', pomodoro: true },
    ];
    const [saving, setSaving] = useState(false);

    const toggleDay = (d) => {
        setForm(p => {
            const has = p.days_of_week.includes(d);
            if (has && p.days_of_week.length === 1) return p; // keep at least one
            return { ...p, days_of_week: has ? p.days_of_week.filter(x => x !== d) : [...p.days_of_week, d] };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const base = {
            id: initial?.id ?? 0,
            period_id: periodId,
            start_time: form.start_time,
            end_time: form.end_time,
            slot_type: form.slot_type,
            subject_id: (form.subject_id !== '' && form.subject_id !== 'autre') ? form.subject_id : null,
            label: (form.subject_id !== '' && form.subject_id !== 'autre') ? null : form.label,
            frequency: form.frequency,
            repetitions: form.slot_type === 'disponible' ? form.repetitions : 1,
        };
        await Promise.all(form.days_of_week.map(day =>
            apiClient(`${API}?action=save_slot`, {
                method: 'POST',
                body: JSON.stringify({ ...base, day_of_week: day }),
            })
        ));
        setSaving(false);
        onSave();
    };

    const handleDelete = async () => {
        if (!initial?.id || !window.confirm('Supprimer ce créneau ?')) return;
        await apiClient(`${API}?action=delete_slot`, {
            method: 'POST',
            body: JSON.stringify({ id: initial.id }),
        });
        onDelete();
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Jour{isNew ? 's' : ''}
                </label>
                {isNew ? (
                    <div className="flex flex-wrap gap-1.5">
                        {DAYS_FULL.slice(1).map((d, i) => {
                            const dayNum = i + 1;
                            const active = form.days_of_week.includes(dayNum);
                            return (
                                <button key={dayNum} type="button" onClick={() => toggleDay(dayNum)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                                        active
                                            ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/40'
                                            : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                                    }`}>
                                    {DAYS[dayNum]}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <select value={form.days_of_week[0]} onChange={e => setForm(p => ({ ...p, days_of_week: [+e.target.value] }))}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm">
                        {DAYS_FULL.slice(1).map((d, i) => <option key={i+1} value={i+1}>{d}</option>)}
                    </select>
                )}
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {form.slot_type === 'sommeil' ? 'Coucher (ce jour)' : 'Début'}
                    </label>
                    <input required type="time" value={form.start_time} onChange={e => handleStartChange(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {form.slot_type === 'sommeil' ? 'Lever (lendemain)' : 'Fin'}
                    </label>
                    <input required type="time" value={form.end_time} onChange={e => {
                        const dur = calcDurMin(form.start_time, e.target.value);
                        const match = seancePaliers.find(p => p.min === dur);
                        setForm(p => ({ ...p, end_time: e.target.value, repetitions: match?.n ?? 1 }));
                    }}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm" />
                    {form.slot_type === 'sommeil' && (
                        <p className="text-xs text-indigo-400 mt-1">Se termine le lendemain matin</p>
                    )}
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <div className="flex flex-wrap gap-2">
                    {SLOT_TYPES.map(t => (
                        <button key={t.value} type="button"
                            onClick={() => handleTypeChange(t.value)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                                form.slot_type === t.value ? t.color : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                            }`}>
                            {t.label}
                        </button>
                    ))}
                </div>
                {form.slot_type === 'disponible' && (
                    <div className="mt-2">
                        <p className="text-xs text-gray-400 mb-1.5">Durée</p>
                        <div className="flex gap-2">
                            {seancePaliers.map(p => (
                                <button key={p.n} type="button"
                                    onClick={() => setForm(f => ({ ...f, end_time: addMinutes(f.start_time, p.min), repetitions: p.n }))}
                                    className={`flex-1 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                                        form.repetitions === p.n
                                            ? p.pomodoro
                                                ? 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/40'
                                                : 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/40'
                                            : 'border-gray-200 dark:border-white/10 text-gray-500 hover:border-gray-300'
                                    }`}>
                                    {p.n} séance{p.n > 1 ? 's' : ''} · {p.label}
                                    {p.pomodoro && <span className="block text-[10px] opacity-70">Pomodoro complet</span>}
                                </button>
                            ))}
                        </div>
                        {form.repetitions === 4 && (
                            <p className="text-xs text-orange-500 dark:text-orange-400 mt-1.5 flex items-center gap-1">
                                🛋 Pause longue de 20 min ajoutée automatiquement après
                            </p>
                        )}
                        {(() => {
                            const q = revisionQuality(form.start_time);
                            if (q === 'optimal') return <p className="text-xs text-green-500 mt-1.5">✓ Plage optimale pour réviser</p>;
                            if (q === 'late')    return <p className="text-xs text-orange-400 mt-1.5">⚠ Créneau tardif — le cerveau retient moins bien</p>;
                            return null;
                        })()}
                    </div>
                )}
            </div>
            {form.slot_type !== 'cours' && form.slot_type !== 'sommeil' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label (optionnel)</label>
                    <input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                        placeholder="ex: Tennis, Piscine…"
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400" />
                </div>
            )}
            {form.slot_type === 'cours' && (
                <>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matière enseignée</label>
                        <select value={form.subject_id}
                            onChange={e => setForm(p => ({ ...p, subject_id: e.target.value, label: (e.target.value === 'autre' || e.target.value === '') ? p.label : '' }))}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm">
                            <option value="">— Choisir une matière —</option>
                            {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            <option value="autre">Autre…</option>
                        </select>
                    </div>
                    {form.subject_id === 'autre' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom de la matière *</label>
                            <input required value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                                placeholder="ex: Philo, NSI, Latin…"
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400" />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fréquence</label>
                        <div className="flex gap-2">
                            {[
                                { value: 'weekly',        label: 'Chaque semaine' },
                                { value: 'biweekly_odd',  label: 'Semaines impaires' },
                                { value: 'biweekly_even', label: 'Semaines paires' },
                            ].map(f => (
                                <button key={f.value} type="button"
                                    onClick={() => setForm(p => ({ ...p, frequency: f.value }))}
                                    className={`flex-1 px-2 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                                        form.frequency === f.value
                                            ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/40'
                                            : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                                    }`}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
            <div className="flex justify-between items-center pt-2">
                {initial?.id ? (
                    <ActionBtn size="md" variant="danger" onClick={handleDelete}>Supprimer</ActionBtn>
                ) : <div />}
                <div className="flex gap-3">
                    <ActionBtn size="md" onClick={onClose}>Annuler</ActionBtn>
                    <ActionBtn size="md" variant="primary" type="submit" disabled={saving}>
                        {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </ActionBtn>
                </div>
            </div>
        </form>
    );
}

// ── Calendrier hebdomadaire (positionnement par heure) ───────────────────────
const HOUR_PX = 48;

function toMin(t) {
    if (!t) return 0;
    const [h, m] = t.slice(0, 5).split(':').map(Number);
    return h * 60 + m;
}

function CalendarGrid({ slots, onClickSlot, onAddSlot }) {
    const [weekParity, setWeekParity] = useState('odd'); // 'odd' | 'even'

    // Plage horaire dynamique depuis les créneaux sommeil
    const crossesMidnight = (s) => toMin(s.end_time) < toMin(s.start_time);
    const sommeils = slots.filter(s => s.slot_type === 'sommeil');
    const wakeUps  = sommeils.map(s => toMin(s.end_time)).filter(m => m > 0 && m <= 12 * 60);
    // Couchers après minuit (ex: 01:00) — inclure dans calStart
    const lateBeds = sommeils.filter(s => crossesMidnight(s) && toMin(s.start_time) <= 4 * 60 && toMin(s.start_time) > 0).map(s => toMin(s.start_time));
    const bedtimes = sommeils.map(s => toMin(s.start_time)).filter(m => m >= 12 * 60);
    const earliestWake = wakeUps.length > 0 ? Math.min(...wakeUps) - 60 : 6 * 60;
    const calStart = Math.max(0, lateBeds.length > 0 ? Math.min(earliestWake, Math.min(...lateBeds)) : earliestWake);
    const calEnd   = bedtimes.length > 0 ? Math.min(24 * 60, Math.max(...bedtimes) + 60) : 24 * 60;

    const minToTop  = (min) => Math.max(0, (min - calStart) * HOUR_PX / 60);
    const durToPx   = (sMin, eMin) => {
        let dur = eMin - sMin;
        if (dur <= 0) dur += 24 * 60;
        return Math.max(18, Math.min(dur, calEnd - calStart) * HOUR_PX / 60);
    };
    const CONTAINER_H = (calEnd - calStart) * HOUR_PX / 60;
    const hours = [];
    for (let h = Math.floor(calStart / 60); h <= Math.ceil(calEnd / 60); h++) hours.push(h);

    // Filtrage bi-hebdomadaire : weekly toujours + la parité sélectionnée
    const hasBiweekly = slots.some(s => s.frequency && s.frequency !== 'weekly');
    const visibleSlots = slots.filter(s =>
        s.frequency === 'weekly' || !s.frequency ||
        (weekParity === 'odd'  && s.frequency === 'biweekly_odd')  ||
        (weekParity === 'even' && s.frequency === 'biweekly_even')
    );

    const slotsByDay = {};
    for (let d = 1; d <= 7; d++) slotsByDay[d] = [];
    visibleSlots.forEach(s => { if (slotsByDay[s.day_of_week]) slotsByDay[s.day_of_week].push(s); });

    // Sommeil de la nuit précédente (indépendant du filtre parité)
    const allSommeils = {};
    for (let d = 1; d <= 7; d++) allSommeils[d] = null;
    slots.filter(s => s.slot_type === 'sommeil').forEach(s => { allSommeils[s.day_of_week] = s; });

    const fmtH = (h) => `${String(h % 24).padStart(2,'0')}h`;
    const fmtMin = (min) => `${String(Math.floor(min / 60)).padStart(2,'0')}:${String(min % 60).padStart(2,'0')}`;

    return (
        <div>
            {/* Header sticky : toggle parité + jours */}
            <div className="sticky top-0 z-20 bg-white dark:bg-gray-900 pb-1 mb-1">
                {hasBiweekly && (
                    <div className="flex gap-2 mb-2">
                        <span className="text-xs text-gray-400 self-center">Afficher :</span>
                        {[{ v: 'odd', l: 'Semaines impaires' }, { v: 'even', l: 'Semaines paires' }].map(({ v, l }) => (
                            <button key={v} onClick={() => setWeekParity(v)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                                    weekParity === v
                                        ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/40'
                                        : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                                }`}>
                                {l}
                            </button>
                        ))}
                    </div>
                )}
                {/* Noms des jours + compteur de séances */}
                <div className="flex min-w-[640px]">
                    <div className="w-10 shrink-0" />
                    {DAYS.slice(1).map((day, idx) => {
                        const dayNum = idx + 1;
                        const dayRevisions = (slotsByDay[dayNum] || []).filter(s => s.slot_type === 'disponible');
                        const totalSeances = dayRevisions.reduce((acc, s) => acc + (Math.floor(durationMin(s.start_time, s.end_time) / 30) || 1), 0);
                        return (
                            <div key={idx} className="flex-1 text-center py-1 border-b border-gray-100 dark:border-white/[0.06]">
                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">{day}</div>
                                {totalSeances > 0 && (
                                    <div className="text-[10px] pt-1 text-cyan-500 dark:text-cyan-400 font-medium leading-tight">
                                        {totalSeances} révisions
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="flex min-w-[640px]" style={{ height: CONTAINER_H }}>

                    {/* Axe horaire */}
                    <div className="w-10 shrink-0 relative" style={{ height: CONTAINER_H }}>
                        {hours.map(h => (
                            <div key={h} style={{ position: 'absolute', top: minToTop(h * 60) }}
                                className="text-[10px] text-gray-400 leading-none pr-1 text-right w-full select-none">
                                {fmtH(h)}
                            </div>
                        ))}
                    </div>

                    {/* Colonnes jours */}
                    {DAYS.slice(1).map((day, idx) => {
                        const dayNum     = idx + 1;
                        const prevDayNum = dayNum === 1 ? 7 : dayNum - 1;
                        const daySlots   = [...(slotsByDay[dayNum] || [])].sort((a, b) => a.start_time.localeCompare(b.start_time));

                        // Continuité du sommeil de la nuit précédente (debut calendrier → heure de lever)
                        const prevSommeil  = allSommeils[prevDayNum];
                        const mornEndRaw   = prevSommeil ? toMin(prevSommeil.end_time) : 0;
                        const isMorning    = prevSommeil && crossesMidnight(prevSommeil);
                        const mornEnd      = isMorning && mornEndRaw > calStart ? mornEndRaw : 0;

                        // Calcul des gaps (repos) — part de la fin du sommeil matin si présent
                        const gaps = [];
                        let prevEnd = mornEnd > 0 ? mornEnd : calStart;
                        daySlots.forEach(slot => {
                            const sMin = Math.max(toMin(slot.start_time), calStart);
                            if (sMin > prevEnd) gaps.push({ start: prevEnd, end: sMin });
                            let eMin = toMin(slot.end_time);
                            if (slot.slot_type === 'sommeil' && eMin < toMin(slot.start_time)) eMin = calEnd;
                            prevEnd = Math.min(Math.max(prevEnd, eMin), calEnd);
                        });
                        if (prevEnd < calEnd) gaps.push({ start: prevEnd, end: calEnd });

                        return (
                            <div key={dayNum} className="flex-1 relative border-l border-gray-100 dark:border-white/[0.06]"
                                style={{ height: CONTAINER_H }}>

                                {/* pas d'en-tête ici, déplacé dans le header sticky */}

                                {/* Continuité sommeil matin (nuit précédente) */}
                                {mornEnd > 0 && (() => {
                                    const info = slotTypeInfo('sommeil');
                                    const top  = minToTop(calStart);
                                    const h    = durToPx(calStart, mornEnd);
                                    return (
                                        <button onClick={() => onClickSlot(prevSommeil)}
                                            style={{ top, height: h, left: 1, right: 1 }}
                                            className={`absolute rounded-lg border px-1.5 py-0.5 text-left text-[11px] font-medium overflow-hidden hover:opacity-90 transition-opacity z-10 ${info.color}`}>
                                            <div className="font-semibold leading-tight truncate">
                                                🌙 → {formatTime(prevSommeil.end_time)}
                                            </div>
                                        </button>
                                    );
                                })()}

                                {/* Lignes horaires */}
                                {hours.slice(0, -1).map(h => (
                                    <div key={h} style={{ top: minToTop(h * 60) }}
                                        className="absolute left-0 right-0 border-t border-gray-100 dark:border-white/[0.04]" />
                                ))}

                                {/* Zones repos (gaps) */}
                                {gaps.map((g, i) => {
                                    const top = minToTop(g.start);
                                    const h   = durToPx(g.start, g.end);
                                    return (
                                        <button key={i}
                                            onClick={() => onAddSlot(dayNum, fmtMin(g.start))}
                                            style={{ top, height: h, left: 1, right: 1 }}
                                            className="absolute rounded text-[10px] text-gray-300 dark:text-white/10 bg-gray-50 dark:bg-white/[0.015] hover:bg-gray-100 dark:hover:bg-white/[0.04] flex items-center justify-center transition-colors group">
                                            {h > 28 && <span className="opacity-0 group-hover:opacity-100 transition-opacity">+</span>}
                                        </button>
                                    );
                                })}

                                {/* Créneaux */}
                                {daySlots.map(slot => {
                                    const info       = slotTypeInfo(slot.slot_type);
                                    const sMin       = Math.max(toMin(slot.start_time), calStart);
                                    const rawEnd     = toMin(slot.end_time);
                                    const isSleep    = slot.slot_type === 'sommeil';
                                    const isRevision = slot.slot_type === 'disponible';
                                    const isLinked   = !!slot.parent_slot_id;
                                    const eMin       = isSleep && rawEnd < toMin(slot.start_time) ? calEnd : Math.min(rawEnd, calEnd);
                                    const top        = minToTop(sMin);
                                    const h          = durToPx(sMin, eMin);
                                    const dur        = durationMin(slot.start_time, slot.end_time);
                                    const isBi       = slot.frequency && slot.frequency !== 'weekly';
                                    const seances    = isRevision ? (slot.repetitions || Math.floor(dur / 30) || 1) : 0;
                                    const quality    = isRevision ? revisionQuality(slot.start_time) : null;
                                    const qualityBar = quality === 'optimal' ? 'bg-green-400' : quality === 'late' ? 'bg-orange-400' : 'bg-cyan-300';
                                    return (
                                        <button key={slot.id}
                                            onClick={() => !isLinked && onClickSlot(slot)}
                                            style={{ top, height: h, left: 1, right: 1 }}
                                            className={`absolute rounded-lg border text-left text-[11px] font-medium overflow-hidden transition-opacity z-10 flex ${info.color} ${isLinked ? 'cursor-default opacity-75' : 'hover:opacity-90'}`}>
                                            {isRevision && <div className={`w-1 shrink-0 rounded-l-lg ${qualityBar}`} />}
                                            {isLinked && <div className="w-1 shrink-0 rounded-l-lg bg-orange-400" />}
                                            <div className="flex-1 px-1.5 py-0.5 min-w-0">
                                                <div className="font-semibold leading-tight truncate">
                                                    {isLinked ? '🛋 ' : isSleep ? '🌙 ' : ''}{formatTime(slot.start_time)}
                                                    {isSleep ? ` → ${formatTime(slot.end_time)}${crossesMidnight(slot) ? ' +1j' : ''}` : ''}
                                                </div>
                                                {h > 32 && (
                                                    <div className="truncate leading-tight opacity-80">
                                                        {isLinked ? 'Pause Pomodoro' : isSleep ? 'Sommeil' : (slot.label || slot.subject_name || info.label)}
                                                    </div>
                                                )}
                                                {h > 46 && !isSleep && !isLinked && (
                                                    <div className="opacity-50 leading-tight">
                                                        {isRevision ? `${seances} séance${seances > 1 ? 's' : ''}` : `${dur}min`}{isBi ? ' · ÷2' : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

        </div>
    );
}

// ── Onboarding guidé (3 étapes) ──────────────────────────────────────────────
function OnboardingGuide({ onStep }) {
    const steps = [
        {
            icon: '🌙',
            title: 'Ton sommeil',
            desc: 'Commence par définir tes horaires de coucher et de lever. C\'est la base de tout.',
            type: 'sommeil',
            hint: null,
        },
        {
            icon: '📚',
            title: 'Tes cours fixes',
            desc: 'Ajoute tes cours, sport et autres engagements hebdomadaires.',
            type: 'cours',
            hint: null,
        },
        {
            icon: '📖',
            title: 'Tes créneaux de révision',
            desc: 'Réserve 2 ou 3 fenêtres de 30 min. Les meilleures plages : 6h30–11h et 15h–19h.',
            type: 'disponible',
            hint: 'Commence léger — 2 créneaux suffisent pour la première semaine.',
        },
    ];

    return (
        <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-6">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Par où commencer ?</p>
            <p className="text-xs text-gray-400 mb-5">Construis ton emploi du temps en 3 étapes, dans l'ordre.</p>
            <div className="flex flex-col sm:flex-row gap-3">
                {steps.map((step, i) => (
                    <button key={i} onClick={() => onStep(step.type)}
                        className="flex-1 text-left rounded-2xl border border-gray-200 dark:border-white/10 p-4 hover:border-cyan-400 dark:hover:border-cyan-500 hover:bg-cyan-50/50 dark:hover:bg-cyan-900/10 transition-all group">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">{step.icon}</span>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Étape {i + 1}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-white mb-1">{step.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{step.desc}</p>
                        {step.hint && (
                            <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-2 font-medium">{step.hint}</p>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ── Import emploi du temps (photo / PDF → IA) ────────────────────────────────
function ImportModal({ periodId, subjects, onImported, onClose }) {
    const [analyzing, setAnalyzing] = useState(false);
    const [extracted, setExtracted] = useState(null);
    const [selected, setSelected]   = useState({});
    const [saving, setSaving]       = useState(false);
    const [error, setError]         = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);

    const matchSubject = (name) => subjects.find(s =>
        s.name.toLowerCase().includes(name.toLowerCase()) ||
        name.toLowerCase().includes(s.name.toLowerCase())
    );

    const handleFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPreviewUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
        setAnalyzing(true);
        setError('');
        setExtracted(null);

        const base64 = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = ev => resolve(ev.target.result.split(',')[1]);
            reader.readAsDataURL(file);
        });

        const res = await apiClient(`${API}?action=import_timetable`, {
            method: 'POST',
            body: JSON.stringify({ period_id: periodId, file_data: base64, mime_type: file.type }),
        }).then(r => r.json());

        setAnalyzing(false);
        if (res.success) {
            const slots = (res.slots || []).map(s => ({
                ...s,
                subject_id: matchSubject(s.subject)?.id ?? null,
            }));
            setExtracted(slots);
            const sel = {};
            slots.forEach((_, i) => { sel[i] = true; });
            setSelected(sel);
        } else {
            setError(res.message || 'Erreur d\'analyse.');
        }
    };

    const save = async () => {
        const toSave = extracted.filter((_, i) => selected[i]);
        if (!toSave.length) return;
        setSaving(true);
        await Promise.all(toSave.map(slot =>
            apiClient(`${API}?action=save_slot`, {
                method: 'POST',
                body: JSON.stringify({
                    id: 0,
                    period_id: periodId,
                    day_of_week: slot.day_of_week,
                    start_time:  slot.start_time,
                    end_time:    slot.end_time,
                    slot_type:   'cours',
                    label:       slot.subject_id ? '' : slot.subject,
                    subject_id:  slot.subject_id || null,
                    frequency:   slot.frequency  || 'weekly',
                }),
            })
        ));
        setSaving(false);
        onImported();
    };

    const nbSelected = Object.values(selected).filter(Boolean).length;

    return (
        <div className="space-y-4">
            <p className="text-xs text-gray-400">
                Importez une photo ou un PDF de votre emploi du temps. L'IA extraira automatiquement les cours.
            </p>

            <label className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl p-6 cursor-pointer hover:border-cyan-400 dark:hover:border-cyan-500 transition-colors">
                <span className="text-3xl">📄</span>
                <span className="text-sm text-gray-500">Cliquer pour choisir une photo ou un PDF</span>
                <span className="text-xs text-gray-400">JPG, PNG, WebP, PDF</span>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleFile} />
            </label>

            {previewUrl && (
                <img src={previewUrl} alt="aperçu" className="max-h-48 w-full object-contain rounded-xl border border-gray-100 dark:border-white/10" />
            )}

            {analyzing && (
                <div className="text-center text-sm text-gray-400 py-4 flex items-center justify-center gap-2">
                    <span className="animate-spin">⟳</span> Analyse IA en cours…
                </div>
            )}

            {error && <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{error}</p>}

            {extracted && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {extracted.length} cours détectés
                        </p>
                        <button onClick={() => { const a = {}; extracted.forEach((_,i) => { a[i]=true; }); setSelected(a); }}
                            className="text-xs text-cyan-500 hover:text-cyan-400">Tout sélectionner</button>
                    </div>

                    <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                        {extracted.map((slot, i) => (
                            <label key={i} className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer transition-colors ${
                                selected[i] ? 'bg-cyan-50 dark:bg-cyan-900/20' : 'bg-gray-50 dark:bg-white/[0.02] opacity-50'
                            }`}>
                                <input type="checkbox" checked={!!selected[i]}
                                    onChange={e => setSelected(p => ({ ...p, [i]: e.target.checked }))}
                                    className="rounded shrink-0" />
                                <span className="text-xs text-gray-400 w-7 shrink-0">{DAYS[slot.day_of_week]}</span>
                                <span className="text-xs text-gray-500 w-24 shrink-0 font-mono">{slot.start_time}–{slot.end_time}</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 truncate">{slot.subject}</span>
                                {slot.subject_id && <span className="text-xs text-green-500 shrink-0">✓ lié</span>}
                                {slot.frequency !== 'weekly' && (
                                    <span className="text-xs text-purple-400 shrink-0">÷2</span>
                                )}
                            </label>
                        ))}
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <ActionBtn size="md" onClick={onClose}>Annuler</ActionBtn>
                        <ActionBtn size="md" variant="primary" onClick={save} disabled={saving || nbSelected === 0}>
                            {saving ? 'Import…' : `Importer (${nbSelected})`}
                        </ActionBtn>
                    </div>
                </div>
            )}
        </div>
    );
}


// ── Composant principal ──────────────────────────────────────────────────────
export default function WeeklyCadreView({ user, onExit, onRegisterTabActions }) {
    const [periods, setPeriods]         = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [slots, setSlots]             = useState([]);
    const [subjects, setSubjects]       = useState([]);
    const [loading, setLoading]         = useState(true);
    const { setBandeau, resetBandeau } = useBandeau();

    useEffect(() => {
        setBandeau({ breadcrumb: null, title: 'Planning' });
        return () => resetBandeau();
    }, [setBandeau, resetBandeau]);
    const [aiAdvice, setAiAdvice]         = useState(null);
    const [adviceState, setAdviceState]   = useState('idle'); // 'idle'|'loading'|'loaded'|'error'
    const [adviceOutdated, setAdviceOutdated] = useState(false);
    const hasAdviceRef                    = useRef(false); // stable ref pour loadData (évite stale closure)
    const [showPeriodForm, setShowPeriodForm] = useState(false);
    const [editPeriod, setEditPeriod]   = useState(null);
    const [slotModal, setSlotModal]     = useState(null); // { slot, defaultDay }
    const [showImport, setShowImport]   = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [canImport, setCanImport]     = useState(false);
    const [canAdvice, setCanAdvice]     = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!onRegisterTabActions) return;
        onRegisterTabActions(
            <div className="flex gap-2">
                <ActionBtn onClick={() => canImport ? setShowImport(true) : setShowUpgrade(true)}
                    icon={!canImport && (
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-500 shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-2.5 h-2.5"><path d="M6 2h12l4 6-10 14L2 8z"/></svg>
                        </span>
                    )}>
                    📷 Importer EDT
                </ActionBtn>
                <ActionBtn onClick={() => navigate('/notes')}>
                    📊 Notes & Objectifs
                </ActionBtn>
            </div>
        );
        return () => onRegisterTabActions(null);
    }, [canImport, onRegisterTabActions]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedPeriod = periods.find(p => p.id === selectedPeriodId);

    const loadData = useCallback(async (periodId) => {
        if (!periodId) return;
        setLoading(true);
        const data = await apiClient(`${API}?action=data&period_id=${periodId}`).then(r => r.json());
        if (data.success) {
            setPeriods(data.periods);
            setSlots(data.slots || []);
            setSubjects(data.subjects || []);
            setCanImport(data.can_import ?? false);
            setCanAdvice(data.can_advice ?? false);
            if (data.advice) {
                setAiAdvice(data.advice);
                setAdviceState('loaded');
                setAdviceOutdated(false);
                hasAdviceRef.current = true;
            } else if (hasAdviceRef.current) {
                // Planning modifié : garde l'ancien conseil visible, marque comme périmé
                setAdviceOutdated(true);
            } else {
                // Premier chargement sans cache : déclenche l'auto-fetch
                setAiAdvice(null);
                setAdviceState('idle');
                setAdviceOutdated(false);
            }
        }
        setLoading(false);
    }, []);

    // Initialisation : charge les périodes et auto-sélectionne la période active ou la plus récente
    useEffect(() => {
        (async () => {
            setLoading(true);
            const data = await apiClient(`${API}?action=data`).then(r => r.json());
            if (data.success && data.periods?.length > 0) {
                const today = new Date().toISOString().slice(0, 10);
                const active = data.periods.find(p => p.start_date <= today && p.end_date >= today);
                setSelectedPeriodId((active ?? data.periods[data.periods.length - 1]).id);
                // setPeriods + slots chargés par l'effet sur selectedPeriodId
            } else {
                setPeriods(data.periods || []);
                setLoading(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadData(selectedPeriodId); }, [selectedPeriodId, loadData]);

    // Reset du conseil quand on change de période
    useEffect(() => {
        setAiAdvice(null);
        setAdviceState('idle');
        setAdviceOutdated(false);
        hasAdviceRef.current = false;
    }, [selectedPeriodId]);

    // Déclenchement automatique — seulement au premier chargement sans cache (pas après une modif)
    useEffect(() => {
        if (loading || !selectedPeriodId || !canAdvice) return;
        if (adviceState !== 'idle' || adviceOutdated) return;
        const types = new Set(slots.map(s => s.slot_type));
        if (!types.has('sommeil') || !types.has('cours') || !types.has('disponible')) return;
        setAdviceState('loading');
        (async () => {
            try {
                const res = await apiClient(`${API}?action=get_advice`, {
                    method: 'POST',
                    body: JSON.stringify({ period_id: selectedPeriodId }),
                }).then(r => r.json());
                if (res.success) {
                    setAiAdvice(res.advice);
                    setAdviceState('loaded');
                    setAdviceOutdated(false);
                    hasAdviceRef.current = true;
                } else {
                    setAdviceState('error');
                }
            } catch {
                setAdviceState('error');
            }
        })();
    }, [loading, slots, selectedPeriodId, adviceState, adviceOutdated]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshAdvice = () => {
        setAdviceOutdated(false);
        setAdviceState('idle');
    };

    const weekCount = selectedPeriod ? Math.ceil(
        (new Date(selectedPeriod.end_date) - new Date(selectedPeriod.start_date)) / (7 * 86400000)
    ) : 0;

    return (
        <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[80vh]">
            <div className="flex flex-col lg:flex-row gap-6">

                {/* ── Panneau périodes ────────────────────────────────────────── */}
                <aside className="w-full lg:w-64 shrink-0">
                    <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Périodes</h2>
                            <ActionBtn variant="add" onClick={() => { setEditPeriod(null); setShowPeriodForm(true); }}>+ Nouvelle</ActionBtn>
                        </div>
                        {loading && periods.length === 0 ? (
                            <div className="text-xs text-gray-400 py-4 text-center">Chargement…</div>
                        ) : periods.length === 0 ? (
                            <p className="text-xs text-gray-400 py-4 text-center">Aucune période.<br/>Créez votre première période.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {periods.map(p => (
                                    <div key={p.id}
                                        className={`rounded-xl px-3 py-2.5 cursor-pointer transition-all border text-sm ${
                                            selectedPeriodId === p.id
                                                ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-700 dark:text-cyan-300'
                                                : 'border-transparent hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300'
                                        }`}
                                        onClick={() => setSelectedPeriodId(p.id)}>
                                        <div className="font-medium truncate">{p.name}</div>
                                        <div className="text-xs opacity-60 mt-0.5">
                                            {new Date(p.start_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} →{' '}
                                            {new Date(p.end_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })}
                                        </div>
                                        <button className="text-xs opacity-40 hover:opacity-70 mt-1"
                                            onClick={e => { e.stopPropagation(); setEditPeriod(p); setShowPeriodForm(true); }}>
                                            Modifier
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </aside>

                {/* ── Zone principale ─────────────────────────────────────────── */}
                <div className="flex-1 min-w-0 space-y-6">
                    {!selectedPeriod ? (
                        <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-8 text-center text-gray-400">
                            Sélectionnez une période pour définir votre semaine type.
                        </div>
                    ) : (
                        <>
                            {/* En-tête période */}
                            <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-4">
                                <h2 className="font-semibold text-gray-800 dark:text-white">{selectedPeriod.name}</h2>
                                <p className="text-sm text-gray-500">
                                    {new Date(selectedPeriod.start_date).toLocaleDateString('fr-FR', { dateStyle: 'long' })} →{' '}
                                    {new Date(selectedPeriod.end_date).toLocaleDateString('fr-FR', { dateStyle: 'long' })}
                                    {' '}<span className="opacity-60">({weekCount} semaine{weekCount > 1 ? 's' : ''})</span>
                                </p>
                            </div>

                            {/* Conseil IA — réservé premium */}
                            {canAdvice && (adviceState === 'loading' || adviceState === 'loaded') && (
                                <div className={`rounded-2xl border px-4 py-3 flex gap-3 items-start transition-all ${
                                    adviceState === 'loading'
                                        ? 'bg-indigo-50/60 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-500/20'
                                        : adviceOutdated
                                            ? 'bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/10'
                                            : 'bg-indigo-50 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-500/30'
                                }`}>
                                    <span className="text-lg shrink-0 mt-0.5">{adviceState === 'loading' ? '⟳' : '✨'}</span>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm leading-relaxed ${
                                            adviceState === 'loading'
                                                ? 'text-indigo-400 dark:text-indigo-500 animate-pulse'
                                                : adviceOutdated
                                                    ? 'text-gray-500 dark:text-gray-400'
                                                    : 'text-indigo-800 dark:text-indigo-200'
                                        }`}>
                                            {adviceState === 'loading' ? 'Axio analyse ton planning…' : aiAdvice}
                                        </p>
                                        {adviceOutdated && adviceState !== 'loading' && (
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-full px-2 py-0.5">
                                                    Planning modifié
                                                </span>
                                                <button
                                                    onClick={refreshAdvice}
                                                    className="text-[11px] text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium transition-colors"
                                                >
                                                    Rafraîchir le conseil →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Onboarding quand la période est vide */}
                            {!loading && slots.length === 0 && (
                                <OnboardingGuide onStep={type => setSlotModal({ slot: null, defaultDay: 1, defaultStartTime: '08:00', defaultSlotType: type })} />
                            )}

                            {/* Grille hebdomadaire */}
                            <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-4">
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Semaine type</h3>
                                {loading ? (
                                    <div className="text-center py-8 text-gray-400 text-sm">Chargement des créneaux…</div>
                                ) : (
                                    <CalendarGrid
                                        slots={slots}
                                        onClickSlot={slot => setSlotModal({ slot, defaultDay: slot.day_of_week })}
                                        onAddSlot={(day, startTime) => setSlotModal({ slot: null, defaultDay: day, defaultStartTime: startTime })}
                                    />
                                )}
                            </div>

                        </>
                    )}
                </div>
            </div>

            {/* ── Modales ─────────────────────────────────────────────────────── */}
            {showPeriodForm && (
                <Modal isOpen={true} title={editPeriod ? 'Modifier la période' : 'Nouvelle période'} onClose={() => setShowPeriodForm(false)}>
                    <PeriodForm
                        initial={editPeriod}
                        onSave={id => { setShowPeriodForm(false); setSelectedPeriodId(id); loadData(id); }}
                        onClose={() => setShowPeriodForm(false)}
                        onDelete={() => { setShowPeriodForm(false); setSelectedPeriodId(null); loadData(null); }}
                    />
                </Modal>
            )}

            {slotModal !== null && (
                <Modal isOpen={true} title={slotModal.slot ? 'Modifier le créneau' : 'Nouveau créneau'} onClose={() => setSlotModal(null)}>
                    <SlotForm
                        initial={slotModal.slot ? { ...slotModal.slot, day_of_week: slotModal.slot.day_of_week } : { day_of_week: slotModal.defaultDay, start_time: slotModal.defaultStartTime, slot_type: slotModal.defaultSlotType }}
                        periodId={selectedPeriodId}
                        subjects={subjects}
                        onSave={() => { setSlotModal(null); loadData(selectedPeriodId); }}
                        onClose={() => setSlotModal(null)}
                        onDelete={() => { setSlotModal(null); loadData(selectedPeriodId); }}
                    />
                </Modal>
            )}

            {showImport && (
                <Modal isOpen={true} title="Importer un emploi du temps" onClose={() => setShowImport(false)}>
                    <ImportModal
                        periodId={selectedPeriodId}
                        subjects={subjects}
                        onImported={() => { setShowImport(false); loadData(selectedPeriodId); }}
                        onClose={() => setShowImport(false)}
                    />
                </Modal>
            )}

            {showUpgrade && (
                <Modal isOpen={true} title="Fonctionnalité Premium" onClose={() => setShowUpgrade(false)}>
                    <div className="space-y-5">
                        <div className="flex flex-col items-center text-center gap-2 py-2">
                            <span className="text-5xl">📷</span>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Import automatique d'emploi du temps</h3>
                            <p className="text-sm text-gray-500 max-w-xs">
                                Prenez en photo votre emploi du temps papier ou importez un PDF. L'IA extrait automatiquement tous vos cours en quelques secondes.
                            </p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-2xl p-4 border border-purple-100 dark:border-purple-500/20 space-y-2">
                            <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">Inclus dans l'offre Premium</p>
                            {[
                                '📷 Import photo / PDF de l\'emploi du temps',
                                '✨ Génération IA des séquences de révision',
                                '📊 Suivi complet des compétences',
                                '🤖 Génération de cours IA',
                            ].map((f, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-purple-800 dark:text-purple-200">
                                    <span>{f}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setShowUpgrade(false)}
                                className="px-4 py-2 rounded-xl text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5">
                                Plus tard
                            </button>
                            <button onClick={() => { setShowUpgrade(false); navigate('/abonnement'); }}
                                className="px-5 py-2 rounded-xl text-sm bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-semibold shadow-md transition-all">
                                Voir les offres →
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

        </div>
    );
}
